/**
 * Serialize formula groups / configs to versioned clipboard payloads
 * and parse incoming JSON back into typed payloads.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { migrateGroupWeightsToPerLayer } from '@trafficpulse/shared';
import type {
  FormulaConfig,
  FormulaGroup,
} from '@trafficpulse/shared';

import {
  FORMULA_CLIPBOARD_KIND,
  FORMULA_CLIPBOARD_SUPPORTED_VERSIONS,
  FORMULA_CLIPBOARD_VERSION,
  type FormulaClipboardPayload,
  type FormulaConfigPayload,
  type FormulaGroupPayload,
} from './formula-clipboard.types';

export function serializeGroupPayload(group: FormulaGroup): FormulaGroupPayload {
  return {
    name: group.name,
    dateRange: group.dateRange,
    dateRangeCustomFrom: group.dateRangeCustomFrom,
    dateRangeCustomTo: group.dateRangeCustomTo,
    mode: group.mode,
    sliders: group.sliders,
    blocks: group.blocks,
    expression: group.expression,
    appliesTo: group.appliesTo,
    ...(group.ignoredPathBankIds?.length
      ? { ignoredPathBankIds: [...group.ignoredPathBankIds] }
      : {}),
  };
}

export function buildGroupClipboardPayload(group: FormulaGroup): FormulaClipboardPayload {
  return {
    kind: FORMULA_CLIPBOARD_KIND,
    version: FORMULA_CLIPBOARD_VERSION,
    type: 'group',
    copiedAt: new Date().toISOString(),
    label: group.name?.trim() || 'Untitled group',
    data: serializeGroupPayload(group),
  };
}

export function buildFormulaClipboardPayload(formula: FormulaConfig): FormulaClipboardPayload {
  const groups = formula.groups.map(serializeGroupPayload);
  // Persist per-layer weights (v2). migrateGroupWeightsToPerLayer also upgrades
  // any legacy flat weight, so the copied payload is always per-layer.
  const perLayer = migrateGroupWeightsToPerLayer(formula.groupWeights, formula.groups);
  const weights = formula.groups.map((g) => perLayer[g.id] ?? {});
  const data: FormulaConfigPayload = { groups, groupWeights: weights };
  return {
    kind: FORMULA_CLIPBOARD_KIND,
    version: FORMULA_CLIPBOARD_VERSION,
    type: 'formula',
    copiedAt: new Date().toISOString(),
    label: groups.length === 1 ? groups[0].name : `${groups.length} groups`,
    data,
  };
}

export function payloadToJson(payload: FormulaClipboardPayload): string {
  return JSON.stringify(payload, null, 2);
}

export function parseClipboardJson(text: string): FormulaClipboardPayload | null {
  if (!text || typeof text !== 'string') return null;
  try {
    const raw = JSON.parse(text) as unknown;
    return assertPayload(raw);
  } catch {
    return null;
  }
}

function assertPayload(raw: unknown): FormulaClipboardPayload | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  if (p.kind !== FORMULA_CLIPBOARD_KIND) return null;
  if (
    typeof p.version !== 'number' ||
    !FORMULA_CLIPBOARD_SUPPORTED_VERSIONS.includes(p.version)
  ) {
    return null;
  }
  if (p.type !== 'group' && p.type !== 'formula') return null;
  if (!p.data || typeof p.data !== 'object') return null;
  if (p.type === 'group') {
    if (!isGroupPayload(p.data)) return null;
  } else if (!isFormulaPayload(p.data)) return null;
  return p as unknown as FormulaClipboardPayload;
}

/** Exported for the library envelope (rule-set/package payloads reuse the
 *  same kind+version wrapper and delegate these two data checks here). */
export function isGroupPayload(value: unknown): value is FormulaGroupPayload {
  if (!value || typeof value !== 'object') return false;
  const g = value as Record<string, unknown>;
  return typeof g.name === 'string' && (g.mode === 'sliders' || g.mode === 'blocks');
}

export function isFormulaPayload(value: unknown): value is FormulaConfigPayload {
  if (!value || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  return Array.isArray(f.groups) && Array.isArray(f.groupWeights);
}
